import React, { useContext } from 'react';

import './EstimatedTradeCostWidgetContent.scss';
import classNames from 'classnames';
import { DetailsPanel } from 'utils/components/DetailsPanel/DetailsPanel';
import DetailsPanelValue from 'utils/components/DetailsPanel/DetailsPanelValue';
import AppContext from 'app/AppContext';
import { EstimatedTradeCostApiResponse } from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApiResponse';
import isNil from 'lodash/isNil';
import { useWidgetContext } from 'widgets/WidgetContext';
import InfoBox from 'utils/components/InfoBox/InfoBox';
import {
    EstimatedTradeCostOrderDataState,
    extractErrorMessage,
} from 'widgets/estimatedTradeCost/store/data/EstimateTradeCostDataState';
import { WarningPanel } from 'utils/components/WarningPanel/WarningPanel';
import { ToolbarIcon } from '@instinet-analytics/semantic-ui-components';
import { Tooltip } from '@instinet-analytics/core-lib';
import { EstimatedTradeCostWidgetRequestPanel } from 'widgets/estimatedTradeCost/widget/components/EstimatedTradeCostWidgetRequestPanel';
import { formatResponseTime } from 'widgets/estimatedTradeCost/store/data/mapping';
import {
    endTimeHelp,
    riskHelp,
    startTimeHelp,
    totalCostHelp,
} from 'widgets/estimatedTradeCost/widget/components/help/property-help';

export function EstimatedTradeCostWidgetContent({
    data,
    editedQty,
    onRerunQuery,
    onQtyChanged,
}: {
    data: EstimatedTradeCostOrderDataState | undefined;
    editedQty: number | undefined;
    onRerunQuery: () => void;
    onQtyChanged: (q: number) => void;
}) {
    const { width } = useWidgetContext();

    const inProgress = data?.status === 'In Progress';
    const failed = data?.status === 'Failed';
    const completed = data?.status === 'Completed';
    const cssClasses = classNames(
        'EstimatedTradeCostWidgetContent',
        inProgress && 'in-progress',
        width <= 225 && 'narrow'
    );

    return (
        <div className={cssClasses}>
            {inProgress && <InProgressPanel message={'Running...'} />}
            {failed && <ErrorPanel data={data} />}
            {completed && !editedQty && (
                <ResponseTitlePanel response={data.response!} />
            )}
            <div className="response-details-panel">
                <EstimatedTradeCostWidgetRequestPanel
                    editedQty={editedQty}
                    data={data}
                    onQtyChanged={onQtyChanged}
                />
                {completed && !editedQty && (
                    <ResponsePanel response={data.response!} />
                )}
                {!!editedQty && <RerunPanel onClick={onRerunQuery} />}
                {!editedQty &&
                    !data?.request.specifiedQty &&
                    !data?.request.orderQtyRemaining && (
                        <div className="non-zero-warning">
                            <InfoBox
                                info={'Update the quantity to a non-zero value'}
                            />
                        </div>
                    )}
            </div>
        </div>
    );
}

function InProgressPanel({ message }: { message: string }) {
    return (
        <div className="InProgressPanel info-box">
            <div className="ui active inline loader mini" />
            <span className="message">{message}</span>
        </div>
    );
}

function RerunPanel({ onClick }: { onClick: () => void }) {
    return (
        <div className="RerunPanel" onClick={onClick}>
            <ToolbarIcon
                className="refresh-button"
                name="refresh"
                tooltip="Refresh with updated quantity"
            />
            <Tooltip text="Refresh with updated quantity">
                <div>Refresh</div>
            </Tooltip>
        </div>
    );
}

function ErrorPanel({ data }: { data: EstimatedTradeCostOrderDataState }) {
    const message = extractErrorMessage(data);
    return (
        <div className="ErrorPanel">
            <WarningPanel text={message} />
        </div>
    );
}

function ResponseTitlePanel({
    response,
}: {
    response: EstimatedTradeCostApiResponse;
}) {
    const { numberFormat } = useContext(AppContext);

    const { bm_cost: totalCost, current_risk: currentRisk } =
        response.results[0].summary;

    return (
        <div className="ResponseTitlePanel">
            <ResponseTitlePanelDetail
                label="Cost"
                value={numberFormat.n2(totalCost)}
                className="total-cost"
                tooltip={totalCostHelp}
            />
            <ResponseTitlePanelDetail
                label="Risk"
                value={numberFormat.n2(currentRisk)}
                className="current-risk"
                tooltip={riskHelp}
            />
        </div>
    );
}

function ResponsePanel({
    response,
}: {
    response: EstimatedTradeCostApiResponse;
}) {
    const { numberFormat, timeFormat } = useContext(AppContext);

    const display = (n: number, value: number) =>
        isNil(value) ? 'No result' : numberFormat.n(n, value);

    const {
        intv_eff_pov2: efPart,
        intv_eff_commence: startTime,
        intv_eff_complete: endTime,
    } = response.results[0].summary;

    return (
        <DetailsPanel className="ResponsePanel">
            <DetailsPanelValue
                label="Participation"
                value={display(2, efPart * 100) + '%'}
                className="eff-part"
            />
            <DetailsPanelValue
                label="Start Time"
                value={formatResponseTime(timeFormat, startTime)}
                tooltip={startTimeHelp}
            />
            <DetailsPanelValue
                label="End Time"
                value={formatResponseTime(timeFormat, endTime)}
                tooltip={endTimeHelp}
            />
        </DetailsPanel>
    );
}

function ResponseTitlePanelDetail({
    label,
    value,
    className,
    tooltip,
}: {
    label: string;
    value: string;
    className: string;
    tooltip: string;
}) {
    return (
        <Tooltip
            className={`ResponseTitlePanelDetail ${className}`}
            text={tooltip}
        >
            <div className="value">{value}</div>
            <div className="label">{label}</div>
        </Tooltip>
    );
}
